import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Select from '../Select';
import {
  PaginationWrapper,
  PaginationInfo,
  PaginationActions,
  PageButton,
  PageIcon,
} from './styled';
import images from 'assets/sheets/images';

const Pagination = ({
  total = 10,
  page = 1,
  pageSize = 10,
  onChangePage,
  pageSizeOptions = [10, 20, 50, 100],
}) => {
  const totalPages = Math.ceil(total / pageSize);
  const [hoverEllipsis, setHoverEllipsis] = useState(null); // 'left' | 'right' | null

  const handlePageChange = (newPage, pageSize) => {
    if (newPage >= 1 && newPage <= totalPages) {
      onChangePage(newPage, pageSize);
    }
  };

  const handlePageSizeChange = ({ value }) => {
    onChangePage(1, value);
  };

  const jumpLeft = () => {
    const target = Math.max(1, page - 5);
    handlePageChange(target, pageSize);
  };

  const jumpRight = () => {
    const target = Math.min(totalPages, page + 5);
    handlePageChange(target, pageSize);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 3;
    const EllipsisLeft = (
      <PageButton
        key="start-ellipsis"
        className="ellipsis"
        onMouseEnter={() => setHoverEllipsis('left')}
        onMouseLeave={() => setHoverEllipsis(null)}
        onClick={jumpLeft}
        aria-label="jump previous"
      >
        {hoverEllipsis === 'left' ? <images.doubleScrollLeft.src /> : '•••'}
      </PageButton>
    );
    const EllipsisRight = (
      <PageButton
        key="end-ellipsis"
        className="ellipsis"
        onMouseEnter={() => setHoverEllipsis('right')}
        onMouseLeave={() => setHoverEllipsis(null)}
        onClick={jumpRight}
        aria-label="jump next"
      >
        {hoverEllipsis === 'right' ? <images.doubleScrollRight.src /> : '•••'}
      </PageButton>
    );

    // 1) Up to 5 pages: show all
    if (totalPages <= maxPagesToShow + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(
          <PageButton
            key={i}
            onClick={() => handlePageChange(i, pageSize)}
            className={page === i ? 'active' : ''}
            disabled={page === i}
          >
            {i}
          </PageButton>
        );
      }
    } else if (totalPages >= 6 && totalPages <= 8) {
      pageNumbers.push(
        <PageButton
          key={1}
          onClick={() => handlePageChange(1, pageSize)}
          className={page === 1 ? 'active' : ''}
          disabled={page === 1}
        >
          1
        </PageButton>
      );

      // Determine side for the single ellipsis
      const showStartEllipsis = page > 4; // closer to end
      const showEndEllipsis = !showStartEllipsis; // closer to start or middle

      // Middle window
      let startPage = Math.max(2, page - 1);
      let endPage = Math.min(totalPages - 1, page + 1);

      if (showEndEllipsis) {
        // near the start: lock to 2..4
        startPage = 2;
        endPage = Math.min(4, totalPages - 1);
      } else {
        // near the end: lock to (N-3)..(N-1)
        startPage = Math.max(2, totalPages - 3);
        endPage = totalPages - 1;
      }

      if (showStartEllipsis) {
        pageNumbers.push(EllipsisLeft);
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(
          <PageButton
            key={i}
            onClick={() => handlePageChange(i, pageSize)}
            className={page === i ? 'active' : ''}
            disabled={page === i}
          >
            {i}
          </PageButton>
        );
      }

      if (showEndEllipsis) {
        pageNumbers.push(EllipsisRight);
      }

      pageNumbers.push(
        <PageButton
          key={totalPages}
          onClick={() => handlePageChange(totalPages, pageSize)}
          className={page === totalPages ? 'active' : ''}
          disabled={page === totalPages}
        >
          {totalPages}
        </PageButton>
      );
    } else {
      // 3) > 8 pages: always show two ellipses (left and right)
      pageNumbers.push(
        <PageButton
          key={1}
          onClick={() => handlePageChange(1, pageSize)}
          className={page === 1 ? 'active' : ''}
          disabled={page === 1}
        >
          1
        </PageButton>
      );
      // Always left ellipsis
      pageNumbers.push(EllipsisLeft);

      // Sliding 3-page window
      let startPage = Math.max(2, page - 1);
      let endPage = Math.min(totalPages - 1, page + 1);

      // Guard: ensure exactly 3 pages if possible
      if (endPage - startPage + 1 < maxPagesToShow) {
        if (startPage === 2) {
          endPage = Math.min(totalPages - 1, startPage + (maxPagesToShow - 1));
        } else if (endPage === totalPages - 1) {
          startPage = Math.max(2, endPage - (maxPagesToShow - 1));
        }
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(
          <PageButton
            key={i}
            onClick={() => handlePageChange(i, pageSize)}
            className={page === i ? 'active' : ''}
            disabled={page === i}
          >
            {i}
          </PageButton>
        );
      }
      // Always right ellipsis
      pageNumbers.push(EllipsisRight);

      pageNumbers.push(
        <PageButton
          key={totalPages}
          onClick={() => handlePageChange(totalPages, pageSize)}
          className={page === totalPages ? 'active' : ''}
          disabled={page === totalPages}
        >
          {totalPages}
        </PageButton>
      );
    }

    return pageNumbers;
  };

  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <PaginationWrapper>
      <PaginationInfo>
        {total} 件中 {from} 〜 {to} 件
      </PaginationInfo>

      <PaginationActions>
        <PageIcon
          onClick={() => handlePageChange(page - 1, pageSize)}
          disabled={page === 1}
        >
          {page === 1 ? <images.left.src /> : <images.leftActive.src />}
        </PageIcon>
        {renderPageNumbers()}
        <PageIcon
          onClick={() => handlePageChange(page + 1, pageSize)}
          disabled={page === totalPages}
        >
          {page === totalPages ? (
            <images.right.src />
          ) : (
            <images.rightActive.src />
          )}
        </PageIcon>
      </PaginationActions>
      <Select
        widthSelect={124}
        $heightSelect={32}
        value={pageSize}
        onChange={handlePageSizeChange}
        options={pageSizeOptions.map((size) => ({
          value: size,
          label: `${size}件/ページ`,
        }))}
        className={'h-[32px]'}
        classNameValue="!text-[#000000E0]"
        classNameSelect="!rounded-[6px] !pl-2 !pr-[4px] min-w-[120px] !h-8"
        classNameSelectRow="!rounded-[6px] !px-[12px]"
        pagination={true}
        $iconDown={images.icon.src}
      />
    </PaginationWrapper>
  );
};

Pagination.propTypes = {
  total: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  onChangePage: PropTypes.func.isRequired,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
};

export default Pagination;
